<script>
  import { format } from 'svelte-i18n';
  import { Popover } from '@sveltestrap/sveltestrap';

  import { PersonCell } from '@client/pages/insights/activity/cell-formatters';

  import { ReportsService } from '@client/services/reports';

  import { appStore } from '@client/stores/app';
  import { recipientsStore } from '@client/stores/recipients';

  import { lazyload } from '@client/actions/lazy-load';

  /**
   * @typedef {Object} ActivityNote
   * @property {string} [ua]
   * @property {string} [isp]
   * @property {string} [country]
   * @property {string} [location]
   * @property {string} [filename]
   * @property {string} [hash]
   * @property {string} [url]
   * @property {string} [linkname]
   * @property {string} [page_title]
   * @property {string} [page_url]
   * @property {string} [objtype]
   * @property {string} [name]
   * @property {string|number} [slide]
   * @property {string} [adid]

   * @typedef {Object} ActivityEntry
   * @property {string} [verb]
   * @property {string} [objecttype]
   * @property {string} [ip]
   * @property {string} [recipient]
   * @property {ActivityNote} [note]
   */

  /**
   * A single activity entry.
   * @type {ActivityEntry}
   */
  export let entry = {};

  /**
   * All entries for the given date (used to show full context in PersonCell).
   * @type {ActivityEntry[]}
   */
  export let entries = [];

  /**
   * Index of the current entry in the list.
   * @type {number}
   */
  export let index;

  /**
   * The date string representing the group this entry belongs to.
   * @type {string}
   */
  export let date;

  /**
   * The recipient being rendered (can be a specific email or 'all').
   * @type {string}
   */
  export let recipient;

  /**
   * Fetches and caches a recipient's report based on email.
   * Returns cached data if available, otherwise fetches from ReportsService.
   * @param {string} email
   * @returns {Promise<any>}
   */
  const fetchRecipientReport = async (email) => {
    const cachedProfile = $recipientsStore[email];

    if (!cachedProfile) {
      $recipientsStore[email] = {};

      const response = await ReportsService.getRecipientReport({ email });
      $recipientsStore[email] = response;

      return response;
    }

    return $recipientsStore[email];
  };

  /**
   * Whether the recipient element is visible (used for lazy-loading).
   * @type {boolean}
   */
  let isVisible = false;

  /**
   * Called when the recipient span intersects with the viewport.
   * Sets `isVisible` to true so content can be loaded.
   */
  const onIntersect = () => {
    isVisible = true;
  };
</script>

{#if recipient === 'all' && !entry.recipient}
  <!-- Bizcard visitors are anonymous, so the entry carries no recipient to profile. Label it
  the way the To: column and the map marker do instead of lazy-loading a report for ''. -->
  <span class="recip">{$format('label.BIZCARD_ANONYMOUS_VISITOR')}</span> -
{:else if recipient === 'all'}
  <span class="recip" use:lazyload={{ onIntersect, rootMargin: '300px 0px', renderVisible: $appStore.mobile }}>
    {#if isVisible}
      {#await fetchRecipientReport(entry.recipient)}
        {entry.recipient}
      {:then}
        {#if $recipientsStore[entry.recipient]?.person?.name?.fullName}
          <span id="popover-{index}-{new Date(date).getTime()}">
            <span>{$recipientsStore[entry.recipient].person.name.fullName}</span>
            <small class="email">(@{entry.recipient.split('@').pop()})</small>
          </span>
          <Popover
            class="recipient-popover"
            target="popover-{index}-{new Date(date).getTime()}"
            trigger="hover"
            position="right"
            title=""
          >
            <PersonCell email={entry.recipient} activity={entries} showCompanyLogo showExpanded />
          </Popover>
        {:else}
          {entry.recipient}
        {/if}
      {:catch}
        {entry.recipient}
      {/await}
    {:else}
      <span class="skeleton">{$format('label.LOADING')}...</span>
    {/if}
  </span> -
{/if}

<style lang="scss" src="./item-recipient.scss"></style>
